// @ts-check
/** @typedef { import('./menuUtils.js').ActiveFilter } ActiveFilter */ 
/** @typedef { import('./menuUtils.js').FilterLabel } FilterLabel */
import React from 'react';
import { HiHome } from 'react-icons/hi';
import { TbTimelineEventExclamation } from 'react-icons/tb';
import { LuBriefcaseBusiness } from "react-icons/lu";

import { Link } from 'react-router-dom';

import { useProjectsListContext } from '@datahooks/ProjectsListContext';

import TextPlaceholder from "../general/TextPlaceholder";
import { TestableText, TestableSelect, TestableSelectItem, TestableList, TestableListItem, TestableListItemPrefix, TestableAccordion, TestableAccordionBody, TestableAccordionHeader, TestableChip } from '@components/general/TestableComponents';
import { ENABLE_EVENT_SUMMARY } from '../../config';
import MenuProjectLink from './MenuProjectLink.jsx';
import { makeAbbreviation } from '@datahooks/metadata/DataUtils';
import MenuLinkFilterPopup from './MenuLinkFiltersPopup';

import PropTypes from 'prop-types';

function HomeLink({ inDrawer, isCollapsed }) {
  return (
    <Link to="/home">
      <TestableListItem dataTestId='menulinks-home' className='text-blue-500'>
        <TestableListItemPrefix
          dataTestId='menulinks-home-icon'
          title={isCollapsed && !inDrawer ? "Home" : ""}
          aria-label={isCollapsed && !inDrawer ? "Home" : undefined}>
          <HiHome className="h-10 w-10" />
        </TestableListItemPrefix>
        <TestableText
          dataTestId='menulinks-home-text'
          variant='paragraph'
          className={`${inDrawer || !isCollapsed ? 'block' : 'hidden'} transition-opacity duration-300`}
          color='blue'>
          Home
        </TestableText>
      </TestableListItem>
    </Link>
  );
}

function EventsLink({ inDrawer, isCollapsed }) {
  if (!ENABLE_EVENT_SUMMARY) return null;
  return (
    <>
      <Link to="/events">
        <TestableListItem dataTestId='menulinks-events' className='text-blue-500'>
          <TestableListItemPrefix
            dataTestId='menulinks-events-icon'
            title={isCollapsed && !inDrawer ? "Events" : ""}
            aria-label={isCollapsed && !inDrawer ? "Events" : undefined}>
            <TbTimelineEventExclamation className="h-10 w-10" />
          </TestableListItemPrefix>
          <TestableText
            dataTestId='menulinks-events-text'
            variant='paragraph'
            className={`${inDrawer || !isCollapsed ? 'block' : 'hidden'} transition-opacity duration-300`}
            color='blue'>
            Events
          </TestableText>
        </TestableListItem>
      </Link>
      <hr className="my-2 border-blue-gray-100 dark:border-dark-tremor-border" />
    </>
  );
}

function BusinessViewLink({ inDrawer, isCollapsed }) {
  return (
    <>
      <Link to="/business-view">
        <TestableListItem dataTestId='menulinks-business-view' className='text-blue-500'>
          <TestableListItemPrefix
            dataTestId='menulinks-business-view-icon'
            title={isCollapsed && !inDrawer ? "Business View" : ""}
            aria-label={isCollapsed && !inDrawer ? "Business View" : undefined}>
            <LuBriefcaseBusiness className="h-10 w-10" />
          </TestableListItemPrefix>
          <TestableText
            dataTestId='menulinks-business-view-text'
            variant='paragraph'
            className={`${inDrawer || !isCollapsed ? 'block' : 'hidden'} transition-opacity duration-300`}
            color='blue'>
            Business View
          </TestableText>
        </TestableListItem>
      </Link>
      <hr className="my-2 border-blue-gray-100 dark:border-dark-tremor-border" />
    </>
  );
}

function GroupFilterControls({
  inDrawer, isCollapsed, groupableTags, filterableTags, menuGroupTag, doGroupTagChange, filterLabels, handleFiltered, removeFilter
}) {

  if (groupableTags.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`${inDrawer || !isCollapsed ? 'block' : 'hidden'} flex gap-x-1 transition-opacity duration-300`}>
        <TestableSelect
          dataTestId="menulinks-tag-groupby-select"
          label="Group by"
          value={menuGroupTag}
          onChange={doGroupTagChange}
          className="pt-2"
        >
          <TestableSelectItem dataTestId="menulinks-tag-group-select-item-none" value='None'>None</TestableSelectItem>
          {groupableTags.map(tag => (
            <TestableSelectItem dataTestId={`menulinks-tag-group-select-item_${tag.tag_id}`} key={tag.tag_id} value={tag.tag_slug}>
              {tag.tag_title}
            </TestableSelectItem>
          ))}
        </TestableSelect>
        {filterableTags.length > 0 && (
          <MenuLinkFilterPopup tags={filterableTags} onFiltered={handleFiltered} />
        )}
      </div>
      {(filterLabels.length > 0 && (inDrawer || !isCollapsed)) && (
        <div className="flex flex-wrap gap-1 mt-1">
          {filterLabels.map((filterLabel) => (
            <TestableChip dataTestId='menu-tag-group-filter-chip' className='bg-blue-500' key={filterLabel.label} value={filterLabel.label} onClose={() => removeFilter(filterLabel.filter)} />
          ))}
        </div>
      )}
      <hr className={`${inDrawer || !isCollapsed ? 'block' : 'hidden'} my-2 border-blue-gray-100 dark:border-dark-tremor-border`} />
    </>
  );
}

function ProjectList({
  isLoading, projectsWithTags, menuGroupTag, selectedGroupTag, groupableTags, filteredProjectsByGroup, openKey, handleOpenKeyChange, filteredFlatProjects, inDrawer, isCollapsed
}) {

  if (isLoading || !projectsWithTags) {
    return <TextPlaceholder lines={5} random={false} />;
  }

  if (menuGroupTag !== 'None' && selectedGroupTag && groupableTags.some(t => t.tag_slug === menuGroupTag)) {
    return Array.from(filteredProjectsByGroup.entries()).map(([groupKey, projectList]) => (
      <TestableAccordion dataTestId='menulinks-group-accordion' key={groupKey} open={openKey === groupKey}>
        <TestableAccordionHeader
          dataTestId='menulinks-group-accordion-header'
          icon={null}
          title={groupKey}
          onClick={() => handleOpenKeyChange(groupKey)}
          className={`text-lg font-normal w-full [&>span:last-child]:hidden`}>
            {isCollapsed ? <span className="block w-full text-center">{makeAbbreviation(groupKey, 3)}</span> : groupKey}
        </TestableAccordionHeader>
        <TestableAccordionBody dataTestId='menulinks-group-accordion-body'>
          {projectList.map(p => (
            <MenuProjectLink key={p.projectId} project={p} inDrawer={inDrawer} isCollapsed={isCollapsed} />
          ))}
        </TestableAccordionBody>
      </TestableAccordion>
    ));
  }
  // Flat list with filtering applied
  return filteredFlatProjects.map(project => (
    <MenuProjectLink key={project.projectId} project={project} inDrawer={inDrawer} isCollapsed={isCollapsed} />
  ));
}

const MenuLinks = ({ inDrawer = false, isCollapsed = false }) => {
  const {
    projectsWithTags, isLoading, menuGroupTag, openKey, groupableTags, filterableTags, selectedGroupTag, filterLabels, filteredFlatProjects, filteredProjectsByGroup, doGroupTagChange, handleFiltered, removeFilter, handleOpenKeyChange
  } = useProjectsListContext();

  return (
    <TestableList dataTestId='menulinks' className='min-w-full p-1'>
      <HomeLink inDrawer={inDrawer} isCollapsed={isCollapsed} />
      <hr className="my-2 border-blue-gray-100 dark:border-dark-tremor-border" />
      <EventsLink inDrawer={inDrawer} isCollapsed={isCollapsed} />
      <BusinessViewLink inDrawer={inDrawer} isCollapsed={isCollapsed} />
      <GroupFilterControls
        inDrawer={inDrawer}
        isCollapsed={isCollapsed}
        groupableTags={groupableTags}
        filterableTags={filterableTags}
        menuGroupTag={menuGroupTag}
        doGroupTagChange={doGroupTagChange}
        filterLabels={filterLabels}
        handleFiltered={handleFiltered}
        removeFilter={removeFilter}
      />
      <ProjectList
        isLoading={isLoading}
        projectsWithTags={projectsWithTags}
        menuGroupTag={menuGroupTag}
        selectedGroupTag={selectedGroupTag}
        groupableTags={groupableTags}
        filteredProjectsByGroup={filteredProjectsByGroup}
        openKey={openKey}
        handleOpenKeyChange={handleOpenKeyChange}
        filteredFlatProjects={filteredFlatProjects}
        inDrawer={inDrawer}
        isCollapsed={isCollapsed}
      />
    </TestableList>
  );
};

MenuLinks.propTypes = {
  inDrawer: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

export default MenuLinks;

HomeLink.propTypes = {
  inDrawer: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

EventsLink.propTypes = {
  inDrawer: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

BusinessViewLink.propTypes = {
  inDrawer: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

GroupFilterControls.propTypes = {
  inDrawer: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  groupableTags: PropTypes.array.isRequired,
  filterableTags: PropTypes.array.isRequired,
  menuGroupTag: PropTypes.string.isRequired,
  doGroupTagChange: PropTypes.func.isRequired,
  filterLabels: PropTypes.array.isRequired,
  handleFiltered: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
};

ProjectList.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  projectsWithTags: PropTypes.any,
  menuGroupTag: PropTypes.string.isRequired,
  selectedGroupTag: PropTypes.any,
  groupableTags: PropTypes.array.isRequired,
  filteredProjectsByGroup: PropTypes.any.isRequired,
  openKey: PropTypes.any,
  handleOpenKeyChange: PropTypes.func.isRequired,
  filteredFlatProjects: PropTypes.array.isRequired,
  inDrawer: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};